import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Package,
  Lock,
  Smartphone,
  CreditCard,
  Key,
  Wrench,
  Umbrella,
  ShoppingBag,
  Building2,
  Truck,
  CheckCircle,
  ArrowRight,
  Wifi,
  Battery,
  Cloud,
  Shield,
  BarChart3,
  Settings,
  Users,
  Clock,
  MapPin,
  QrCode,
  Fingerprint,
  ChevronDown,
  ChevronUp,
  Zap,
  Globe,
  Server
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/e632388a9fa75fa0c59634368cb688495f983ad239da7076f53c02c6ae27d92d.png",
  models: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/16a731586325adbee1953082ef1dd136b406df876f4921b4fc7fb18b6777b4a9.png",
  parcel: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/a2f09b394068ed137f052bf8fb96923a9b4bd1e61d38d66fe31a8dd83f8892ab.png",
  tools: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/6054c52ca341c8f4669915b056040002021ed82896534769e00f63a01507b633.png",
  beach: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/37fbb45b381076a522b4fcd303280f6b0b3defa1520b9967560b68e12d8992d9.png",
  dashboard: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/7caf1e725f2c750195c11a4e033c8afd0f0ca939378f0cea09315698bdb38f4e.png"
};

// FAQ Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        className="w-full py-5 flex items-center justify-between text-left"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`faq-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="font-semibold text-slate-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-teal-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 text-slate-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const SmartLockersPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('smartLockersPage.metaTitle')}</title>
        <meta name="description" content={t('smartLockersPage.metaDesc')} />
        <meta name="keywords" content="smart lockers, parcel lockers, smart locker systems, automated lockers, tool lockers, beach lockers, locker systems for businesses, smart parcel lockers, IoT lockers, connected lockers, locker infrastructure, self service lockers" />
        <link rel="canonical" href="https://0gbaltics.com/smart-lockers" />
        <meta property="og:title" content={t('smartLockersPage.metaTitle')} />
        <meta property="og:description" content={t('smartLockersPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="smart-lockers-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartLockersPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('smartLockersPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('smartLockersPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta"
                  >
                    {t('smartLockersPage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Use case badges */}
                <div className="flex flex-wrap gap-3">
                  {[
                    t('smartLockersPage.parcelDelivery'),
                    t('smartLockersPage.toolManagement'),
                    t('smartLockersPage.beachLockers'),
                    t('smartLockersPage.retailPickup'),
                    t('smartLockersPage.keyManagement')
                  ].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Smart Locker Infrastructure - parcel lockers, tool lockers, beach lockers and retail pickup"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT ARE SMART LOCKERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="what-are-lockers">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.whatAreBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.whatAreTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.whatAreDesc1')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('smartLockersPage.whatAreDesc2')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartLockersPage.accessMethods')}</h4>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: Smartphone, text: t('smartLockersPage.accessMobile') },
                    { icon: Key, text: t('smartLockersPage.accessPIN') },
                    { icon: CreditCard, text: t('smartLockersPage.accessRFID') },
                    { icon: QrCode, text: t('smartLockersPage.accessQR') },
                    { icon: Fingerprint, text: t('smartLockersPage.accessBadge') },
                    { icon: CreditCard, text: t('smartLockersPage.accessPayment') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-6">{t('smartLockersPage.cloudCapabilities')}</h4>
                <div className="space-y-4">
                  {[
                    { icon: Lock, title: t('smartLockersPage.capAccessControl'), desc: t('smartLockersPage.capAccessControlDesc') },
                    { icon: Smartphone, title: t('smartLockersPage.capNotifications'), desc: t('smartLockersPage.capNotificationsDesc') },
                    { icon: BarChart3, title: t('smartLockersPage.capUsageTracking'), desc: t('smartLockersPage.capUsageTrackingDesc') },
                    { icon: MapPin, title: t('smartLockersPage.capAvailability'), desc: t('smartLockersPage.capAvailabilityDesc') },
                    { icon: Settings, title: t('smartLockersPage.capAnalytics'), desc: t('smartLockersPage.capAnalyticsDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-4 bg-white p-4 rounded-lg border border-slate-200">
                        <Icon className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" />
                        <div>
                          <h5 className="font-semibold text-slate-900 text-sm">{item.title}</h5>
                          <p className="text-slate-600 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SMART LOCKER MODELS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="locker-models">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('smartLockersPage.modelsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('smartLockersPage.modelsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('smartLockersPage.modelsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                {
                  icon: Building2,
                  title: t('smartLockersPage.towerLockers'),
                  desc: t('smartLockersPage.towerLockersDesc'),
                  features: [t('smartLockersPage.towerFeature1'), t('smartLockersPage.towerFeature2'), t('smartLockersPage.towerFeature3')],
                  color: 'bg-blue-600'
                },
                {
                  icon: ShoppingBag,
                  title: t('smartLockersPage.storeLockers'),
                  desc: t('smartLockersPage.storeLockersDesc'),
                  features: [t('smartLockersPage.storeFeature1'), t('smartLockersPage.storeFeature2'), t('smartLockersPage.storeFeature3')],
                  color: 'bg-teal-600'
                },
                {
                  icon: Package,
                  title: t('smartLockersPage.slingLockers'),
                  desc: t('smartLockersPage.slingLockersDesc'),
                  features: [t('smartLockersPage.slingFeature1'), t('smartLockersPage.slingFeature2'), t('smartLockersPage.slingFeature3')],
                  color: 'bg-purple-600'
                },
                {
                  icon: Battery,
                  title: t('smartLockersPage.batteryPowered'),
                  desc: t('smartLockersPage.batteryPoweredDesc'),
                  features: [t('smartLockersPage.batteryFeature1'), t('smartLockersPage.batteryFeature2'), t('smartLockersPage.batteryFeature3')],
                  color: 'bg-orange-500'
                },
              ].map((model, i) => {
                const Icon = model.icon;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className={`${model.color} p-6`}>
                      <Icon className="h-8 w-8 text-white mb-3" />
                      <h3 className="text-xl font-semibold text-white">{model.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-slate-600 text-sm mb-4">{model.desc}</p>
                      <div className="space-y-2">
                        {model.features.map((feature, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-teal-600" />
                            <span className="text-slate-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <img 
                src={IMAGES.models}
                alt="Smart locker models - Tower, Store, Sling, and Battery powered lockers"
                className="w-full max-w-4xl h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* PARCEL LOCKERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="parcel-lockers">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.parcelLockers')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.parcelLockers')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.parcelLockersDesc')}
                </p>
              </div>
              
              <div>
                <img 
                  src={IMAGES.parcel}
                  alt="Parcel lockers deployed in urban environment for e-commerce pickup"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* TOOL MANAGEMENT LOCKERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="tool-lockers">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <video 
                  src="/assets/videos/xtower-4.mp4"
                  poster={IMAGES.tools}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <img 
                  src={IMAGES.tools}
                  alt="Tool management lockers in industrial factory environment"
                  className="w-full h-auto rounded-lg shadow-lg hidden"
                />
              </div>
              
              <div className="order-1 lg:order-2">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('smartLockersPage.toolLockers')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('smartLockersPage.toolLockers')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('smartLockersPage.toolLockersDesc')}
                </p>
                
                <Link 
                  to="/tool-tracking" 
                  className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 font-medium"
                  data-testid="tool-tracking-link"
                >
                  {t('smartLockersPage.discoverToolTracking')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* RETAIL & BEACH LOCKERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="retail-beach-lockers">
          <div className="container-0g">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Retail Pickup */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-purple-600 p-6">
                  <ShoppingBag className="h-8 w-8 text-white mb-3" />
                  <h3 className="text-2xl font-semibold text-white">{t('smartLockersPage.retailTitle')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">
                    {t('smartLockersPage.retailDesc')}
                  </p>
                  <h4 className="font-semibold text-slate-900 mb-3">{t('smartLockersPage.retailAdvantages')}</h4>
                  <ul className="space-y-2 mb-6">
                    {[
                      t('smartLockersPage.retailAdv1'),
                      t('smartLockersPage.retailAdv2'),
                      t('smartLockersPage.retailAdv3'),
                      t('smartLockersPage.retailAdv4'),
                      t('smartLockersPage.retailAdv5')
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle className="h-4 w-4 text-purple-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500 text-sm">
                    {t('smartLockersPage.retailNote')}
                  </p>
                </div>
              </div>

              {/* Beach Lockers */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-cyan-500 p-6">
                  <Umbrella className="h-8 w-8 text-white mb-3" />
                  <h3 className="text-2xl font-semibold text-white">{t('smartLockersPage.beachTitle')}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-6">
                    {t('smartLockersPage.beachDesc')}
                  </p>
                  <h4 className="font-semibold text-slate-900 mb-3">{t('smartLockersPage.idealLocations')}</h4>
                  <ul className="space-y-2 mb-6">
                    {[
                      t('smartLockersPage.beachLoc1'),
                      t('smartLockersPage.beachLoc2'),
                      t('smartLockersPage.beachLoc3'),
                      t('smartLockersPage.beachLoc4'),
                      t('smartLockersPage.beachLoc5')
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500 text-sm">
                    {t('smartLockersPage.beachNote')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <img 
                src={IMAGES.beach}
                alt="Beach smart lockers at coastal location"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* KEY MANAGEMENT & BATTERY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="key-battery-lockers">
          <div className="container-0g">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Key Management */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Key className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900">{t('smartLockersPage.keyTitle')}</h3>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.keyDesc')}
                </p>
                <div className="space-y-3">
                  {[
                    t('smartLockersPage.keyFeature1'),
                    t('smartLockersPage.keyFeature2'),
                    t('smartLockersPage.keyFeature3'),
                    t('smartLockersPage.keyFeature4'),
                    t('smartLockersPage.keyFeature5'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-amber-500" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Battery Powered */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <Battery className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900">{t('smartLockersPage.batteryTitle')}</h3>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.batteryDesc')}
                </p>
                <div className="space-y-3 mb-6">
                  {[
                    t('smartLockersPage.batteryFeat1'),
                    t('smartLockersPage.batteryFeat2'),
                    t('smartLockersPage.batteryFeat3'),
                    t('smartLockersPage.batteryFeat4'),
                    t('smartLockersPage.batteryFeat5'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <p className="text-green-800 text-sm">
                    <strong>{t('smartLockersPage.batteryUseCases')}</strong> {t('smartLockersPage.batteryUseCasesDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SOFTWARE PLATFORM */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="locker-platform">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.platformBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.platformTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.platformDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartLockersPage.platformCapabilities')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: MapPin, title: t('smartLockersPage.platAvailability'), desc: t('smartLockersPage.platAvailabilityDesc') },
                    { icon: Users, title: t('smartLockersPage.platUserMgmt'), desc: t('smartLockersPage.platUserMgmtDesc') },
                    { icon: Smartphone, title: t('smartLockersPage.platNotifications'), desc: t('smartLockersPage.platNotificationsDesc') },
                    { icon: BarChart3, title: t('smartLockersPage.platAnalytics'), desc: t('smartLockersPage.platAnalyticsDesc') },
                    { icon: Settings, title: t('smartLockersPage.platRemoteConfig'), desc: t('smartLockersPage.platRemoteConfigDesc') },
                    { icon: Shield, title: t('smartLockersPage.platSecurityLogs'), desc: t('smartLockersPage.platSecurityLogsDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-teal-600" />
                          <h5 className="font-semibold text-slate-900 text-sm">{item.title}</h5>
                        </div>
                        <p className="text-slate-600 text-xs">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.dashboard}
                  alt="Locker management platform dashboard"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* API AND INTEGRATIONS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="integrations">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('smartLockersPage.apiBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('smartLockersPage.apiTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('smartLockersPage.apiDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: ShoppingBag, name: t('smartLockersPage.intEcommerce'), desc: t('smartLockersPage.intEcommerceDesc') },
                { icon: Users, name: t('smartLockersPage.intIdentity'), desc: t('smartLockersPage.intIdentityDesc') },
                { icon: CreditCard, name: t('smartLockersPage.intPayment'), desc: t('smartLockersPage.intPaymentDesc') },
                { icon: Truck, name: t('smartLockersPage.intLogistics'), desc: t('smartLockersPage.intLogisticsDesc') },
                { icon: Building2, name: t('smartLockersPage.intSmartCity'), desc: t('smartLockersPage.intSmartCityDesc') },
                { icon: Globe, name: t('smartLockersPage.intERP'), desc: t('smartLockersPage.intERPDesc') },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-slate-800 border border-slate-700 p-6 rounded-lg text-center">
                    <Icon className="h-8 w-8 text-teal-400 mx-auto mb-3" />
                    <h4 className="text-white font-semibold text-sm mb-1">{item.name}</h4>
                    <p className="text-slate-400 text-xs">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SMART LOCKERS FOR CITIES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="lockers-for-cities">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.citiesBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.citiesTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.citiesDesc')}
                </p>
                
                <h4 className="font-semibold text-slate-900 mb-4">{t('smartLockersPage.municipalApps')}</h4>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: Package, text: t('smartLockersPage.cityApp1') },
                    { icon: Users, text: t('smartLockersPage.cityApp2') },
                    { icon: Umbrella, text: t('smartLockersPage.cityApp3') },
                    { icon: Wrench, text: t('smartLockersPage.cityApp4') },
                    { icon: Key, text: t('smartLockersPage.cityApp5') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
                
                <Link 
                  to="/smart-city-iot-infrastructure"
                  className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700"
                >
                  {t('smartLockersPage.learnSmartCity')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
              
              <div className="bg-teal-600 p-8 rounded-lg text-white">
                <h4 className="font-semibold mb-6 text-xl">{t('smartLockersPage.networksInfra')}</h4>
                <p className="text-teal-100 mb-6">
                  {t('smartLockersPage.networksInfraDesc')}
                </p>
                <div className="space-y-3">
                  {[
                    t('smartLockersPage.cityBenefit1'),
                    t('smartLockersPage.cityBenefit2'),
                    t('smartLockersPage.cityBenefit3'),
                    t('smartLockersPage.cityBenefit4'),
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-teal-200" />
                      <span className="text-teal-50">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY 0G BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.whyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.whyTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('smartLockersPage.whyDesc')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: t('smartLockersPage.whyFeature1'), desc: t('smartLockersPage.whyFeature1Desc') },
                    { title: t('smartLockersPage.whyFeature2'), desc: t('smartLockersPage.whyFeature2Desc') },
                    { title: t('smartLockersPage.whyFeature3'), desc: t('smartLockersPage.whyFeature3Desc') },
                    { title: t('smartLockersPage.whyFeature4'), desc: t('smartLockersPage.whyFeature4Desc') },
                    { title: t('smartLockersPage.whyFeature5'), desc: t('smartLockersPage.whyFeature5Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-slate-900">{item.title}</h4>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-900 p-8 rounded-lg">
                  <h4 className="text-white font-semibold mb-6 text-lg">{t('smartLockersPage.relatedSolutions')}</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/asset-tracking', label: t('smartLockersPage.assetTrackingLabel'), desc: t('smartLockersPage.assetTrackingDesc') },
                      { to: '/connectivity', label: t('smartLockersPage.connectivityLabel'), desc: t('smartLockersPage.connectivityDesc') },
                      { to: '/smart-city-iot-infrastructure', label: t('smartLockersPage.smartCityLabel'), desc: t('smartLockersPage.smartCityDesc') },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-slate-800 p-4 rounded-lg hover:bg-slate-700 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-white font-medium">{link.label}</h5>
                            <p className="text-slate-400 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* FAQ */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="faq">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('smartLockersPage.faqBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('smartLockersPage.faqTitle')}
                </h2>
              </div>
              
              <div className="bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-200">
                <FAQItem 
                  question={t('smartLockersPage.faq1Q')}
                  answer={t('smartLockersPage.faq1A')}
                />
                <FAQItem 
                  question={t('smartLockersPage.faq2Q')}
                  answer={t('smartLockersPage.faq2A')}
                />
                <FAQItem 
                  question={t('smartLockersPage.faq3Q')}
                  answer={t('smartLockersPage.faq3A')}
                />
                <FAQItem 
                  question={t('smartLockersPage.faq4Q')}
                  answer={t('smartLockersPage.faq4A')}
                />
                <FAQItem 
                  question={t('smartLockersPage.faq5Q')}
                  answer={t('smartLockersPage.faq5A')}
                />
                <FAQItem 
                  question={t('smartLockersPage.faq6Q')}
                  answer={t('smartLockersPage.faq6A')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('smartLockersPage.ctaTitle')}
            </h2>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-button"
            >
              {t('smartLockersPage.ctaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default SmartLockersPage;
